import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, FileSpreadsheet, FileText, Loader2, Upload, Trash2,
  AlertCircle, CheckCircle2, AlertTriangle, Sparkles, ShieldCheck, Download,
} from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { adminApi } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const ACCEPT = '.docx,.xlsx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export default function AdminQuestionnaireImport() {
  const { t, i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);   // {provider, normalized}
  const [confirming, setConfirming] = useState(false);
  // Sprint A.1.3-γ — Validation Center + Import Summary state.
  const [validation, setValidation] = useState(null); // {issues, summary}
  const [validating, setValidating] = useState(false);
  const [summary, setSummary] = useState(null);       // post-confirm rich payload
  const [summaryOpen, setSummaryOpen] = useState(false);

  const errors = preview?.normalized?.errors || [];
  const warnings = preview?.normalized?.warnings || [];
  const validationIssues = validation?.issues || [];
  const validationBlocking = validation?.summary?.blocking || false;
  const metadata = preview?.normalized?.metadata || {};
  const sections = preview?.normalized?.sections || [];
  const questions = preview?.normalized?.questions || [];

  const downloadTemplate = async (format) => {
    try {
      // Use authenticated API helper so the JWT travels with the request.
      const res = await api.get(`/admin/library/import/template?format=${format}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hm_questionnaire_template_v1.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('toasts.failed'), { description: formatApiError(e) });
    }
  };

  const choose = (selectedFile) => {
    if (!selectedFile) return;
    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error(t('toasts.failed'), { description: 'File too large (>5MB).' });
      return;
    }
    setFile(selectedFile);
    setPreview(null);
    setValidation(null);
    setSummary(null);
    void doPreview(selectedFile);
    // Validation Center runs in parallel for .xlsx files.
    if ((selectedFile.name || '').toLowerCase().endsWith('.xlsx')) {
      void doValidate(selectedFile);
    }
  };

  const doValidate = async (selectedFile) => {
    setValidating(true);
    try {
      const res = await adminApi.validateImport(selectedFile);
      setValidation(res);
    } catch (e) {
      // Non-blocking — validation is best-effort.
      setValidation({
        issues: [{
          level: 'warning', worksheet: '—', row: null, column: null, field: 'request',
          received: null, expected: 'Successful response',
          description: isES ? 'No se pudo contactar el Centro de Validación.' : 'Validation Center could not be reached.',
          recommendation: isES ? 'Puedes apoyarte en el panel de Vista previa a continuación.' : 'You can still rely on the Preview panel below.',
        }],
        summary: { total: 1, errors: 0, warnings: 1, blocking: false },
      });
    } finally {
      setValidating(false);
    }
  };

  const doPreview = async (selectedFile) => {
    setPreviewing(true);
    try {
      const res = await adminApi.importPreview(selectedFile);
      setPreview(res);
    } catch (e) {
      toast.error(t('toasts.failed'), { description: formatApiError(e) });
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const discard = () => {
    setFile(null);
    setPreview(null);
    setValidation(null);
    setSummary(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const confirm = async () => {
    if (!preview) return;
    if (errors.length > 0) {
      toast.error(t('library.import.needsErrorsResolved', { defaultValue: 'Cannot create — preview still has errors.' }));
      return;
    }
    if (validationBlocking) {
      toast.error(t('library.import.validationBlocked', { defaultValue: 'Validation Center reported blocking errors. Please fix them and re-upload.' }));
      return;
    }
    setConfirming(true);
    try {
      const res = await adminApi.importConfirm(preview);
      const qid = res?.questionnaire?.id;
      if (res?.summary) {
        setSummary(res.summary);
        setSummaryOpen(true);
      } else {
        toast.success(t('library.import.confirmedTitle', { defaultValue: 'Draft created' }));
        if (qid) navigate(`/admin/questionnaires/${qid}`);
      }
    } catch (e) {
      toast.error(t('toasts.failed'), { description: formatApiError(e) });
    } finally {
      setConfirming(false);
    }
  };

  const downloadSummaryPdf = async () => {
    if (!summary) return;
    try {
      const blob = await adminApi.exportImportSummaryPdf(summary);
      const url = URL.createObjectURL(blob.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `import_summary_${summary.questionnaire_code || 'questionnaire'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(isES ? 'PDF de resumen descargado' : 'Summary PDF downloaded');
    } catch (e) {
      toast.error(t('toasts.failed'), { description: formatApiError(e) });
    }
  };

  const sectionsByOrder = useMemo(() => {
    const map = {};
    sections.forEach((s) => { map[s.order] = s; });
    return map;
  }, [sections]);

  return (
    <>
      <Topbar
        title={t('library.import.title', { defaultValue: 'Import Questionnaire' })}
        subtitle={t('library.import.subtitle', { defaultValue: 'Build evaluations from a structured template.' })}
      />
      <div className="p-6 lg:p-8 space-y-6" data-testid="admin-import-page">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate('/admin/questionnaires')}
          className="gap-1.5 -mb-2"
          data-testid="admin-import-back"
        >
          <ArrowLeft className="h-4 w-4" /> {t('common.back', { defaultValue: 'Back' })}
        </Button>

        {/* Step 1 — Templates */}
        <Card className="p-6 shadow-card space-y-3" data-testid="admin-import-step1">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-display text-lg font-bold tracking-tight">
                {t('library.import.step1Title', { defaultValue: 'Step 1 — Download the template' })}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t('library.import.step1Sub', { defaultValue: 'Choose your preferred format. Both produce identical questionnaires.' })}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => downloadTemplate('xlsx')} variant="outline" className="gap-1.5" data-testid="import-download-xlsx">
              <FileSpreadsheet className="h-4 w-4" /> {t('library.import.downloadXlsx', { defaultValue: 'Download .xlsx template' })}
            </Button>
            <Button onClick={() => downloadTemplate('docx')} variant="outline" className="gap-1.5" data-testid="import-download-docx">
              <FileText className="h-4 w-4" /> {t('library.import.downloadDocx', { defaultValue: 'Download .docx template' })}
            </Button>
          </div>
        </Card>

        {/* Step 2 — Notes */}
        <Card className="p-6 shadow-card space-y-1" data-testid="admin-import-step2">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {t('library.import.step2Title', { defaultValue: 'Step 2 — Complete it offline' })}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t('library.import.step2Sub', { defaultValue: 'Keep the bracket markers intact. For .xlsx, do not rename the sheets.' })}
          </p>
        </Card>

        {/* Step 3 — Upload */}
        <Card className="p-6 shadow-card space-y-3" data-testid="admin-import-step3">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {t('library.import.step3Title', { defaultValue: 'Step 3 — Upload' })}
          </h2>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => choose(e.target.files?.[0])}
            data-testid="import-file-input"
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => inputRef.current?.click()} className="gap-1.5 bg-gradient-primary" data-testid="import-choose-file">
              <Upload className="h-4 w-4" /> {t('library.import.uploadCta', { defaultValue: 'Choose file (.docx or .xlsx)' })}
            </Button>
            {file && (
              <span className="text-xs text-foreground/80 truncate max-w-md" data-testid="import-file-name">
                {file.name} <span className="text-muted-foreground">· {(file.size / 1024).toFixed(1)} KB</span>
              </span>
            )}
            {preview && (
              <Button onClick={discard} size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" data-testid="import-discard">
                <Trash2 className="h-3.5 w-3.5" /> {t('library.import.discard', { defaultValue: 'Discard preview' })}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t('library.import.uploadHint', { defaultValue: 'Max 5 MB. The file stays on the server only during preview.' })}
          </p>
        </Card>

        {/* Preview */}
        {previewing && (
          <Card className="p-12 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Parsing…
          </Card>
        )}

        {preview && (
          <Card className="p-6 shadow-card space-y-5" data-testid="admin-import-preview">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-bold tracking-tight">
                  {t('library.import.previewTitle', { defaultValue: 'Preview' })}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {t('library.import.previewSub', { defaultValue: 'Nothing is saved until you confirm.' })}
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider">
                <span className="text-muted-foreground">{t('library.import.providerLabel', { defaultValue: 'Parsed by' })}</span>
                <Badge variant="outline" className="font-mono">{preview.provider}</Badge>
              </div>
            </div>

            {/* Validation Center — Sprint A.1.3-γ */}
            {(validating || validationIssues.length > 0) && (
              <div
                className={`rounded-lg border p-3 space-y-2 ${validationBlocking
                  ? 'border-destructive/40 bg-destructive/5'
                  : (validationIssues.length === 0
                    ? 'border-emerald-400/40 bg-emerald-50 dark:bg-emerald-900/10'
                    : 'border-amber-400/40 bg-amber-50 dark:bg-amber-900/10')}`}
                data-testid="validation-center"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs font-semibold">
                    <ShieldCheck className={`h-4 w-4 ${validationBlocking ? 'text-destructive' : 'text-emerald-600'}`} />
                    <span>{t('library.import.validationTitle', { defaultValue: 'Validation Center' })}</span>
                    {validating && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                  </div>
                  {validation?.summary && (
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant="outline" className="border-destructive/40 text-destructive" data-testid="validation-errors-count">
                        {validation.summary.errors} errors
                      </Badge>
                      <Badge variant="outline" className="border-amber-400/50 text-amber-700" data-testid="validation-warnings-count">
                        {validation.summary.warnings} warnings
                      </Badge>
                    </div>
                  )}
                </div>
                {validationIssues.length > 0 && (
                  <div className="overflow-x-auto" data-testid="validation-issues-table">
                    <table className="min-w-full text-xs">
                      <thead className="text-muted-foreground">
                        <tr>
                          <th className="text-left py-1.5 pr-2 font-semibold">{isES ? 'Nivel' : 'Level'}</th>
                          <th className="text-left py-1.5 pr-2 font-semibold">{isES ? 'Hoja' : 'Worksheet'}</th>
                          <th className="text-left py-1.5 pr-2 font-semibold">{isES ? 'Fila · Col' : 'Row · Col'}</th>
                          <th className="text-left py-1.5 pr-2 font-semibold">{isES ? 'Campo' : 'Field'}</th>
                          <th className="text-left py-1.5 pr-2 font-semibold">{isES ? 'Recibido' : 'Received'}</th>
                          <th className="text-left py-1.5 pr-2 font-semibold">{isES ? 'Esperado' : 'Expected'}</th>
                          <th className="text-left py-1.5 pr-2 font-semibold">{isES ? 'Descripción' : 'Description'}</th>
                          <th className="text-left py-1.5 pr-2 font-semibold">{isES ? 'Recomendación' : 'Recommendation'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationIssues.map((iss, idx) => (
                          <tr
                            key={`viss-${idx}`}
                            className="border-t border-border/40 align-top"
                            data-testid={`validation-issue-${idx}`}
                          >
                            <td className="py-1.5 pr-2">
                              <Badge
                                variant="outline"
                                className={iss.level === 'error'
                                  ? 'border-destructive/40 text-destructive'
                                  : 'border-amber-400/50 text-amber-700'}
                                data-testid={`validation-issue-${idx}-level`}
                              >
                                {iss.level}
                              </Badge>
                            </td>
                            <td className="py-1.5 pr-2">{iss.worksheet || '—'}</td>
                            <td className="py-1.5 pr-2 font-mono">
                              {iss.row != null ? iss.row : '—'}{iss.column ? `·${iss.column}` : ''}
                            </td>
                            <td className="py-1.5 pr-2 font-mono">{iss.field || '—'}</td>
                            <td className="py-1.5 pr-2 max-w-[140px] truncate" title={String(iss.received ?? '')}>{String(iss.received ?? '—')}</td>
                            <td className="py-1.5 pr-2 max-w-[180px]">{iss.expected || '—'}</td>
                            <td className="py-1.5 pr-2 max-w-[220px]">{iss.description || '—'}</td>
                            <td className="py-1.5 pr-2 max-w-[220px] text-foreground/80">{iss.recommendation || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!validating && validationIssues.length === 0 && (
                  <p className="text-xs text-emerald-700">
                    {t('library.import.validationClean', { defaultValue: 'No structural issues found — safe to import.' })}
                  </p>
                )}
              </div>
            )}

            {/* Errors */}
            {errors.length > 0 && (
              <div className="rounded-lg border-2 border-destructive/40 bg-destructive/5 p-3 space-y-1.5" data-testid="import-errors">
                <div className="flex items-center gap-2 text-destructive text-xs font-semibold">
                  <AlertCircle className="h-4 w-4" />
                  {t('library.import.errorsTitle', { defaultValue: 'Blocking errors — fix the file before importing' })}
                </div>
                <ul className="text-xs text-foreground/85 list-disc pl-6 space-y-0.5">
                  {errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
              <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 space-y-1.5" data-testid="import-warnings">
                <div className="flex items-center gap-2 text-warning text-xs font-semibold">
                  <AlertTriangle className="h-4 w-4" />
                  {t('library.import.warningsTitle', { defaultValue: 'Warnings' })}
                </div>
                <ul className="text-xs text-foreground/85 list-disc pl-6 space-y-0.5">
                  {warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {/* Metadata grid */}
            <div data-testid="import-metadata">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                {t('library.import.metadataTitle', { defaultValue: 'Metadata' })}
              </p>
              {/* Sprint A.1.2 — Suite codes summary band (prominent). */}
              <div className="mb-3 rounded-lg border border-border/70 bg-muted/30 px-3 py-2" data-testid="import-suite-codes">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                  {t('library.import.suiteCodes.label', { defaultValue: 'Suites' })}
                </p>
                {(metadata.suite_codes && metadata.suite_codes.length > 0) ? (
                  <div className="flex flex-wrap gap-1.5">
                    {metadata.suite_codes.map((s) => (
                      <Badge
                        key={s}
                        variant="outline"
                        className="font-mono text-xs uppercase tracking-wider"
                        data-testid={`import-suite-code-${s}`}
                      >
                        {s}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs italic text-warning" data-testid="import-suite-codes-empty">
                    {t('library.import.suiteCodes.empty', { defaultValue: 'No suite selected. This evaluation will not be assignable until at least one suite is selected.' })}
                  </p>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-2 text-xs">
                {Object.entries(metadata).filter(([k]) => k !== 'suite_codes').map(([k, v]) => (
                  <div key={k} className="flex flex-col gap-0.5 rounded-md border border-border/70 px-2.5 py-1.5">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">{k}</span>
                    <span className="text-foreground/85 whitespace-pre-wrap">{Array.isArray(v) ? v.join(', ') : String(v ?? '—')}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sections */}
            <div data-testid="import-sections">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                {t('library.import.sectionsTitle', { defaultValue: 'Sections' })} · {sections.length}
              </p>
              <ol className="space-y-2">
                {sections.map((s) => (
                  <li key={s.order} className="rounded-md border border-border/70 px-3 py-2 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">#{s.order} · {s.name}</span>
                      <span className="text-muted-foreground">weight {s.weight}</span>
                    </div>
                    {s.description && <p className="mt-1 text-foreground/80">{s.description}</p>}
                  </li>
                ))}
              </ol>
            </div>

            {/* Questions */}
            <div data-testid="import-questions">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                {t('library.import.questionsTitle', { defaultValue: 'Questions' })} · {questions.length}
              </p>
              <ol className="space-y-2.5">
                {questions.map((q, i) => (
                  <li key={i} className="rounded-md border border-border/70 px-3 py-2 text-xs space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium">Q{i + 1} · {q.prompt}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge variant="outline" className="font-mono text-xs">{q.type}</Badge>
                        <Badge variant="outline" className="font-mono text-xs">w {q.weight}</Badge>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Section: {sectionsByOrder[q.section_order]?.name || `#${q.section_order}`}
                    </div>
                    {(q.options || []).length > 0 && (
                      <ul className="mt-1 text-xs list-disc pl-5">
                        {q.options.map((o, oi) => (
                          <li key={oi} className={o.correct ? 'text-success font-medium' : ''}>
                            {o.label} {o.correct && <CheckCircle2 className="inline h-3 w-3" />}
                          </li>
                        ))}
                      </ul>
                    )}
                    {Object.keys(q.competencies || {}).length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        {t('library.import.competencies', { defaultValue: 'Competencies' })}:{' '}
                        {Object.entries(q.competencies).map(([k, w]) => `${k}=${w}`).join(', ')}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={discard} data-testid="import-cancel">
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                onClick={confirm}
                disabled={confirming || errors.length > 0 || validationBlocking}
                className="gap-1.5 bg-gradient-primary"
                data-testid="import-confirm"
              >
                {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {t('library.import.confirmCta', { defaultValue: 'Create draft questionnaire' })}
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* Sprint A.1.3-γ — Import Summary Dashboard */}
      <ImportSummaryDialog
        open={summaryOpen}
        onOpenChange={(o) => {
          setSummaryOpen(o);
          if (!o && summary?.questionnaire_id) {
            navigate(`/admin/questionnaires/${summary.questionnaire_id}`);
          }
        }}
        summary={summary}
        onDownloadPdf={downloadSummaryPdf}
        t={t}
      />
    </>
  );
}

// ---- Import Summary Dashboard component -----------------------------------

function ImportSummaryDialog({ open, onOpenChange, summary, onDownloadPdf, t }) {
  if (!summary) return null;
  const stats = [
    { label: t('library.import.summary.sections', { defaultValue: 'Sections imported' }), value: summary.sections_imported },
    { label: t('library.import.summary.questions', { defaultValue: 'Questions imported' }), value: summary.questions_imported },
    { label: t('library.import.summary.options', { defaultValue: 'Options imported' }), value: summary.options_imported },
    { label: t('library.import.summary.competencies', { defaultValue: 'Competencies linked' }), value: summary.competencies_count },
    { label: t('library.import.summary.warnings', { defaultValue: 'Warnings' }), value: summary.warnings_count },
    { label: t('library.import.summary.time', { defaultValue: 'Processing time' }), value: `${summary.processing_time_ms} ms` },
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="import-summary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {t('library.import.summary.title', { defaultValue: 'Import successful' })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border bg-muted/30 p-3" data-testid="import-summary-headline">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {t('library.import.summary.created', { defaultValue: 'Questionnaire created' })}
            </div>
            <div className="text-sm font-semibold mt-1">{summary.questionnaire_name}</div>
            <div className="text-xs font-mono text-muted-foreground mt-0.5">{summary.questionnaire_code}</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="import-summary-stats">
            {stats.map((s) => (
              <div key={s.label} className="rounded-md border bg-card p-2.5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</div>
                <div className="text-base font-semibold mt-0.5">{s.value}</div>
              </div>
            ))}
          </div>
          {(summary.suite_codes_detected || []).length > 0 && (
            <div data-testid="import-summary-suites">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                {t('library.import.summary.suites', { defaultValue: 'Suite codes detected' })}
              </div>
              <div className="flex flex-wrap gap-1">
                {summary.suite_codes_detected.map((s) => (
                  <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                ))}
              </div>
            </div>
          )}
          {(summary.warnings || []).length > 0 && (
            <div data-testid="import-summary-warnings">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                {t('library.import.summary.warningsList', { defaultValue: 'Warnings' })}
              </div>
              <ul className="text-xs list-disc pl-5 space-y-0.5">
                {summary.warnings.map((w, i) => <li key={`isw-${i}`}>{String(w)}</li>)}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onDownloadPdf} data-testid="import-summary-pdf-btn" className="gap-1.5">
            <Download className="h-4 w-4" /> {t('library.import.summary.pdf', { defaultValue: 'Download as PDF' })}
          </Button>
          <Button onClick={() => onOpenChange(false)} data-testid="import-summary-open-btn">
            {t('library.import.summary.openCta', { defaultValue: 'Open questionnaire' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
